import { useEffect } from 'react';
import { type RouteResource, useResource } from '@atlassian/react-resource-router';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useResourceInvalidateOld = (resource: RouteResource<any>) => {
	const { clear, key } = useResource(resource);

	useEffect(
		() => () => {
			clear();
		},
		[key, clear],
	);
};

/** Cleans the specific router resource on unmount or key change. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useResourceInvalidate = (resource: RouteResource<any>) => {
	const { clear, key = '' } = useResource(resource);
	const previousKey = usePrevious(key);

	useEffect(() => {
		return () => {
			/**
			 * "previousKey" plays an important role for keeping track of cleanup.
			 * Adding this dependency close the gap in the effect discrepancy between strict and non strict mode.
			 *
			 * NON STRICT MODE:
			 * During the initial load: no cleanup effect.
			 *
			 * STRICT MODE:
			 * During initial load: cleanup effect is called multiple times. Resource clearing is prevented due to the previousKey being undefined.
			 *
			 * Both modes:
			 * Transition to other plan: cleanup effect may be called multiple times; Only clearing resource when it has the old key.
			 * Transition to non plan: cleanup effect is called once; clearing the plan resource.
			 * Transition to pages in the same plan: no cleanup effect.
			 */
			if (previousKey && key === previousKey) {
				clear();
			}
		};
	}, [key, clear, previousKey]);
};
